import React, { useState, useEffect } from "react";
import { isAmount } from "../../../utils/num";
import { Button, Modal, Form, Row, Col, Alert } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { addTarget, reset } from "../../../store/actions/performanceSlice";
import UserAutocomplete from "../../../Components/UserAutocomplete/UserAutocomplete";
import UniToast from "../../../Components/UniToast/UniToast";
import { ImgsUrl } from "../../../Api/Api";
import "./AddTarget.css";

const AddTarget = ({ show, onHide, categoryId, onTargetAdded }) => {
  const dispatch = useDispatch();
  const { loading, error, targetAdded } = useSelector((state) => state.performance);

  // Form state
  const [formData, setFormData] = useState({
    userId: null,
    end_time: "",
    start_time: "",
    hours: "",
    lives: "",
    coins: ""
  });

  // Validation state
  const [errors, setErrors] = useState({});
  const [showToast, setShowToast] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (show) {
      setFormData({
        userId: null,
        end_time: "",
        start_time: "",
        hours: "",
        lives: "",
        coins: ""
      });
      setErrors({});
    }
  }, [show]);

  // Handle success/error states
  useEffect(() => {
    if (targetAdded) {
      setShowToast(true);
      // Close modal after successful addition
      setTimeout(() => {
        onHide();
        if (onTargetAdded) onTargetAdded();
      }, 1500);
    }
    if (error) {
      setShowToast(true);
    }
  }, [targetAdded, error, onHide, onTargetAdded]);

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.userId) {
      newErrors.userId = "User selection is required";
    }

    if (!formData.end_time.trim()) {
      newErrors.end_time = "End date is required";
    }

    if (!formData.start_time.trim()) {
      newErrors.start_time = "Start date is required";
    }

    if (!formData.hours.trim()) {
      newErrors.hours = "Hours is required";
    }

    if (!formData.lives.trim()) {
      newErrors.lives = "Lives is required";
    }

    if (!formData.coins.trim()) {
      newErrors.coins = "Coins is required";
    } else if (!isAmount(formData.coins)) {
      newErrors.coins = "Coins must be a positive number (up to 4 decimals)";
    }

    // Both dates come from native <input type="date"> pickers, so they're
    // already well-formed "YYYY-MM-DD" strings - just make sure the range
    // makes sense.
    if (
      formData.start_time &&
      formData.end_time &&
      formData.end_time < formData.start_time
    ) {
      newErrors.end_time = "End date must be on or after the start date";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }
  };

  // Handle user selection from autocomplete
  const handleUserSelect = (user) => {
    handleInputChange("userId", user);
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Prepare form data for API — field names match
    // AdminTargetCategories::addTarget()'s validation.
    const submitData = {
      user_id: formData.userId.id,
      end_time: formData.end_time,
      start_time: formData.start_time,
      hours: formData.hours,
      lives: formData.lives,
      coins: String(formData.coins).trim()
    };

    dispatch(addTarget({ 
      formData: submitData, 
      categoryId: categoryId 
    }));
  };

  // Handle modal close
  const handleClose = () => {
    if (!loading) {
      onHide();
    }
  };

  return (
    <>
      <Modal 
        show={show} 
        onHide={handleClose}
        size="lg"
        centered
        backdrop={loading ? "static" : true}
        keyboard={!loading}
        className="add-target-modal"
      >
        <Modal.Header closeButton={!loading} className="add-target-header">
          <Modal.Title className="add-target-title">
            <div className="d-flex align-items-center gap-3">
              <div className="modal-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              Add New Target
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="add-target-body">
          <Form onSubmit={handleSubmit}>
            <Row>
              {/* User Selection */}
              <Col md={12} className="mb-4">
                <Form.Group>
                  <Form.Label className="form-label">
                    Select User <span className="required">*</span>
                  </Form.Label>
                  <UserAutocomplete
                    value={formData.userId}
                    onChange={handleUserSelect}
                    placeholder="Search and select a user..."
                    className={errors.userId ? "is-invalid" : ""}
                    disabled={loading}
                  />
                  {errors.userId && (
                    <div className="invalid-feedback d-block">
                      {errors.userId}
                    </div>
                  )}
                </Form.Group>
              </Col>

              {/* Time Fields */}
              <Col md={6} className="mb-4">
                <Form.Group>
                  <Form.Label className="form-label">
                    Start Date <span className="required">*</span>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.start_time}
                    onChange={(e) => handleInputChange("start_time", e.target.value)}
                    className={`form-input ${errors.start_time ? "is-invalid" : ""}`}
                    disabled={loading}
                  />
                  {errors.start_time && (
                    <div className="invalid-feedback">
                      {errors.start_time}
                    </div>
                  )}
                </Form.Group>
              </Col>

              <Col md={6} className="mb-4">
                <Form.Group>
                  <Form.Label className="form-label">
                    End Date <span className="required">*</span>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.end_time}
                    onChange={(e) => handleInputChange("end_time", e.target.value)}
                    className={`form-input ${errors.end_time ? "is-invalid" : ""}`}
                    disabled={loading}
                    min={formData.start_time || undefined}
                  />
                  {errors.end_time && (
                    <div className="invalid-feedback">
                      {errors.end_time}
                    </div>
                  )}
                </Form.Group>
              </Col>

              {/* Hours Field */}
              <Col md={6} className="mb-4">
                <Form.Group>
                  <Form.Label className="form-label">
                    Hours <span className="required">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.hours}
                    onChange={(e) => handleInputChange("hours", e.target.value)}
                    className={`form-input ${errors.hours ? "is-invalid" : ""}`}
                    disabled={loading}
                    placeholder="Enter hours (e.g., 8, 4.5)"
                  />
                  {errors.hours && (
                    <div className="invalid-feedback">
                      {errors.hours}
                    </div>
                  )}
                </Form.Group>
              </Col>

              {/* Lives Field */}
              <Col md={6} className="mb-4">
                <Form.Group>
                  <Form.Label className="form-label">
                    Lives <span className="required">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.lives}
                    onChange={(e) => handleInputChange("lives", e.target.value)}
                    className={`form-input ${errors.lives ? "is-invalid" : ""}`}
                    disabled={loading}
                    placeholder="Enter lives count"
                  />
                  {errors.lives && (
                    <div className="invalid-feedback">
                      {errors.lives}
                    </div>
                  )}
                </Form.Group>
              </Col>

              {/* Coins Field */}
              <Col md={12} className="mb-4">
                <Form.Group>
                  <Form.Label className="form-label">
                    Coins <span className="required">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    inputMode="decimal"
                    value={formData.coins}
                    onChange={(e) => handleInputChange("coins", e.target.value)}
                    className={`form-input ${errors.coins ? "is-invalid" : ""}`}
                    disabled={loading}
                    placeholder="Enter coins amount"
                    min="1"
                  />
                  {errors.coins && (
                    <div className="invalid-feedback">
                      {errors.coins}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            {/* Selected User Preview */}
            {formData.userId && (
              <div className="selected-user-card mb-4">
                <div className="d-flex align-items-center gap-3">
                  <div className="user-avatar-preview">
                    {formData.userId.img ? (
                      <img 
                        src={formData.userId.img.startsWith('http') 
                          ? formData.userId.img 
                          : `${ImgsUrl}/${formData.userId.img}`
                        } 
                        alt={formData.userId.name}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className="avatar-placeholder-preview"
                      style={{ display: formData.userId.img ? 'none' : 'flex' }}
                    >
                      {formData.userId.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                  </div>
                  <div className="user-info-preview">
                    <h6 className="user-name-preview">{formData.userId.name ? `@${formData.userId.name}` : ""}</h6>
                    <p className="user-email-preview">{formData.userId.email}</p>
                  </div>
                  <div className="ms-auto">
                    <span className="selected-badge">Selected</span>
                  </div>
                </div>
              </div>
            )}
          </Form>
        </Modal.Body>

        <Modal.Footer className="add-target-footer">
          <Button 
            variant="outline-secondary" 
            onClick={handleClose}
            disabled={loading}
            className="cancel-btn"
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmit}
            disabled={loading || !formData.userId}
            className="submit-btn"
          >
            {loading ? (
              <div className="d-flex align-items-center gap-2">
                <div className="spinner-border spinner-border-sm" role="status"></div>
                Adding Target...
              </div>
            ) : (
              <div className="d-flex align-items-center gap-2">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z"
                    fill="currentColor"
                  />
                </svg>
                Add Target
              </div>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Toast for success and error messages */}
      {showToast && (
        <UniToast
          setOpen={setShowToast}
          open={showToast}
          title={targetAdded ? "Success" : "Error"}
          message={
            targetAdded 
              ? "Target added successfully!" 
              : error || "Failed to add target. Please try again."
          }
          reset={reset}
        />
      )}
    </>
  );
};

export default AddTarget;
